import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useContentfulImage } from 'gatsby-source-contentful/hooks';
import type { IGatsbyImageData } from 'gatsby-plugin-image';

import useContentfulAlignment from '#hooks/useContentfulAlignment';
import useColor from '#hooks/useColor';
import {
  ColorBackground,
} from '#components/Hero/styles';
import type { ContentfulAnimatedImage } from '#components/HeroAnimation';
import HeroAnimation from '#components/HeroAnimation';
import useResizeImage from '#hooks/useResizeImage';
import type { HeroProps } from '#components/Hero';
import useViewport, { viewport } from '#hooks/useViewport';
import type { ImageDefinition } from '#contentful/Image';
import BackgroundImage from '#components/BackgroundImage';
import type { ContentfulVideoAsset } from '@/components/Section/SectionSettings';
import Animations from '@/components/AnimationTexts';
import BackgroundVideo from '@/components/BackgroundImage/BackgroundVideo';

export type HomePageHeroProps = HeroProps & {
  animatedImage?: ContentfulAnimatedImage,
  bgImgMobile: ImageDefinition,
  bgImgTablet: ImageDefinition,
  video: ContentfulVideoAsset
};

type BgImage = IGatsbyImageData | null;
type BgImageList = Record<string, BgImage>;

const HomePageHero = ({
  alignment,
  animatedImage,
  bgImgMobile,
  bgImgTablet,
  overline,
  headline,
  subtitle,
  info,
  image,
  maxTextWidth = '32rem',
  className,
  fontColor = 'implightblue100',
  addOverlay = false,
  isSectionTallest = false,
  extendContentWidth = false,
  noTopMargin = false,
  animationTexts,
  video,
}: HomePageHeroProps) => {
  const { breakpoints, palette } = useTheme();
  const [bgImage, setBgImage] = useState<BgImage>(null);
  const [displayAnimatedImg, setDisplayAnimatedImg] = useState<boolean>(false);
  const [isLaptop, setIsLaptop] = useState<boolean>(false);

  const bgImageMobileResized = useResizeImage(bgImgMobile.image, breakpoints.values.sm);
  const bgImageTabletResized = useResizeImage(bgImgTablet.image, breakpoints.values.md);
  const bgImageDesktopResized = useContentfulImage({
    image: {
      url: image?.url.replace('https:', ''),
      width: breakpoints.values.xl,
      height: isSectionTallest ? 640 : 400,
    },
  });

  const bgImageMobile = bgImageMobileResized;
  const bgImageTablet = bgImageTabletResized;
  const bgImageDesktop = bgImageDesktopResized;
  const bgImageList = {
    xs: bgImageMobile,
    sm: bgImageMobile,
    md: bgImageTablet,
    lg: bgImageDesktop,
    xl: bgImageDesktop,
  } as BgImageList;

  const activeViewport = useViewport();

  useEffect(() => {
    setBgImage(bgImageList[activeViewport]);
    setIsLaptop([viewport.laptop, viewport.desktop].includes(activeViewport));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewport]);

  useEffect(() => {
    const showAnimatedImage = Boolean(animatedImage && isLaptop);
    setDisplayAnimatedImg(showAnimatedImage);
  }, [animatedImage, isLaptop]);

  const itemsAlignment = useContentfulAlignment(alignment);
  const maxWidth = maxTextWidth ?? undefined;
  const desktopFontColor = useColor('color', fontColor);

  const textColor = !isLaptop ? palette.color?.impblue500 : desktopFontColor;
  const colorSubheading = textColor === palette.color?.impblue500
    ? palette.color?.impgray900
    : textColor;

  const textAlignmentDefault = 'left';

  return (
    <Grid
      container
      direction="column"
      justifyContent={{ xs: 'flex-end', lg: 'center' }}
      alignItems={itemsAlignment}
      className={className}
      sx={{
        position: 'relative',
        marginTop: noTopMargin ? 0 : { xs: 7, md: 8 },
        minHeight: {
          xs: isSectionTallest ? '530px' : '100px',
          md: isSectionTallest ? '636px' : '400px',
        },
      }}
    >
      <Box>
        {video && (
        <BackgroundVideo url={video.url} />
        )}
        {(image && bgImage) && <BackgroundImage bgImage={bgImage} minHeight={{ xs: '100%' }} position={{ xs: 'absolute' }} />}
        {displayAnimatedImg && (
          <HeroAnimation image={animatedImage} />
        )}

        {addOverlay && (
          <ColorBackground
            sx={{
              backgroundImage: palette.gradient?.bluegreenlefttoright,
              zIndex: 15,
            }}
          />
        )}

        <Grid
          container
          direction="column"
          sx={{
            width: {
              xs: '90%',
              sm: '25rem',
              md: '30rem',
              lg: extendContentWidth ? '80%' : '40rem',
            },
            pl: {
              xs: 3.5, sm: 5, lg: 10, xl: 17,
            },
            pr: 2,
            mb: { xs: 4, lg: 0 },
            zIndex: 20,
            mt: 0,
            position: 'relative',

            ...(animationTexts && {
              height: '600px',
              display: 'flex',
              justifyContent: 'center',
            }),

          }}
        >
          {overline && (
            <Grid item>
              <Typography
                variant="overline1"
                textAlign={textAlignmentDefault}
                color={colorSubheading}
                sx={{ zIndex: 20 }}
              >
                {overline.toUpperCase()}
              </Typography>
            </Grid>
          )}

          <Grid
            item
            mb={{
              xs: 0, sm: 1, md: 1.5, lg: 2.5,
            }}
          >
            <Typography
              variant="fouxH1"
              textAlign={textAlignmentDefault}
            >
              {headline}
            </Typography>
          </Grid>

          {subtitle && (
            <Grid item>
              <Typography
                variant="subheading1"
                color={colorSubheading}
                textAlign={textAlignmentDefault}
                maxWidth={maxWidth}
              >
                {subtitle}
              </Typography>
            </Grid>
          )}

          {animationTexts && (
          <Animations content={animationTexts} className="w-[400px] md:w-full" />
          )}

          {info && (
            <Grid item>
              <Typography
                variant="subheading1"
                color={colorSubheading}
                textAlign={textAlignmentDefault}
                maxWidth={maxWidth}
                mt={isLaptop ? 2.5 : 0}
              >
                {info}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Grid>
  );
};

export default styled(HomePageHero)({});
